export const LANE_COUNT = 4;
export const RELAY_HEIGHT = 640;
export const RUNNER_Y = 548;
export const ITEM_SIZE = 34;
export const RUNNER_SIZE = 42;
export const PERFECT_INTEGRITY = 3;

export function clampLane(lane) {
  return Math.min(LANE_COUNT - 1, Math.max(0, lane));
}

export function createInitialRelayState(routeIndex = 0, definition) {
  const route = definition.routes[routeIndex];

  return {
    status: 'ready',
    routeIndex,
    routeName: route.name,
    score: 0,
    integrity: 3,
    packets: 0,
    streak: 0,
    bestStreak: 0,
    lane: 1,
    elapsedMs: 0,
    timeRemainingMs: route.durationMs,
    nextItemId: 1,
    items: [],
  };
}

export function createRelayItem(id, route, random = Math.random) {
  const roll = random();
  const blockerBias = route.blockerBias ?? 0.42;
  const type = roll < 0.12
    ? 'boost'
    : roll < blockerBias
      ? 'blocker'
      : 'packet';

  return {
    id,
    type,
    lane: clampLane(Math.floor(random() * LANE_COUNT)),
    y: -ITEM_SIZE,
  };
}

export function calculateRouteGrade(state, route) {
  if (!state || !route) {
    return '-';
  }

  const packetRatio = route.targetPackets > 0 ? state.packets / route.targetPackets : 0;

  if (packetRatio >= 1 && state.integrity >= PERFECT_INTEGRITY && state.bestStreak >= route.targetPackets) {
    return 'S';
  }

  if (packetRatio >= 1 && state.integrity >= 2) {
    return 'A';
  }

  if (packetRatio >= 0.75) {
    return 'B';
  }

  return 'C';
}

export function calculateRouteCompletionBonus(state, route) {
  if (!state || !route) {
    return 0;
  }

  const timeBonus = Math.ceil(Math.max(0, state.timeRemainingMs) / 1000) * 12;
  const integrityBonus = Math.max(0, state.integrity) * 180;
  const streakBonus = state.bestStreak * 24;
  const perfectBonus = state.integrity >= PERFECT_INTEGRITY && state.bestStreak >= route.targetPackets
    ? 650
    : 0;

  return timeBonus + integrityBonus + streakBonus + perfectBonus;
}

export function moveRelayLane(state, direction) {
  if (state.status !== 'playing') {
    return state;
  }

  return {
    ...state,
    lane: clampLane(state.lane + direction),
  };
}

export function updateRelayGame(state, {
  deltaMs,
  route,
  spawnedItems = [],
}) {
  if (state.status !== 'playing') {
    return state;
  }

  const deltaSeconds = deltaMs / 1000;
  const movedItems = state.items
    .concat(spawnedItems)
    .map((item) => ({
      ...item,
      y: item.y + route.speed * deltaSeconds,
    }));
  const runnerTop = RUNNER_Y - RUNNER_SIZE / 2;
  const runnerBottom = RUNNER_Y + RUNNER_SIZE / 2;
  let score = state.score;
  let integrity = state.integrity;
  let packets = state.packets;
  let streak = state.streak;
  let bestStreak = state.bestStreak;
  let timeRemainingMs = Math.max(0, state.timeRemainingMs - deltaMs);
  const remainingItems = [];

  movedItems.forEach((item) => {
    const overlapsRunner = item.lane === state.lane &&
      item.y + ITEM_SIZE / 2 >= runnerTop &&
      item.y - ITEM_SIZE / 2 <= runnerBottom;

    if (overlapsRunner) {
      if (item.type === 'blocker') {
        integrity -= 1;
        streak = 0;
        return;
      }

      if (item.type === 'boost') {
        timeRemainingMs = Math.min(route.durationMs, timeRemainingMs + 3200);
        score += 60 + streak * 8;
        streak += 1;
        bestStreak = Math.max(bestStreak, streak);
        return;
      }

      packets += 1;
      streak += 1;
      bestStreak = Math.max(bestStreak, streak);
      score += 100 + streak * 14;
      return;
    }

    if (item.y > RELAY_HEIGHT + ITEM_SIZE) {
      if (item.type === 'packet') {
        streak = 0;
      }
      return;
    }

    remainingItems.push(item);
  });

  const routeComplete = packets >= route.targetPackets;
  const failed = integrity <= 0 || timeRemainingMs <= 0;
  const completionState = {
    ...state,
    score,
    integrity,
    packets,
    streak,
    bestStreak,
    timeRemainingMs,
  };

  if (routeComplete && !failed) {
    score += calculateRouteCompletionBonus(completionState, route);
  }

  return {
    ...state,
    status: failed ? 'gameOver' : routeComplete ? 'routeComplete' : 'playing',
    elapsedMs: state.elapsedMs + deltaMs,
    timeRemainingMs,
    score,
    integrity,
    packets,
    streak,
    bestStreak,
    items: remainingItems,
    nextItemId: state.nextItemId + spawnedItems.length,
  };
}
